import { createStyles, makeUiStyles, Theme } from '../styles/styles';
import { blendHexWithOverlay, fontDisplay, hexToRgbA, StylesIndexInjections } from 'common-react';
import { LinkSize } from './Link.types';

type VariantType = 'normal' | 'underline';

export interface LinkStyleProps {
  variant: VariantType;
  size: LinkSize;
  disabled?: boolean;
  showExternalIcon?: boolean;
  showDocumentIcon?: boolean;
}

const hasAnyIcon = ({ showExternalIcon, showDocumentIcon }: LinkStyleProps) =>
  showExternalIcon || showDocumentIcon;

const getFontSize = (size: LinkSize, theme: Theme) => {
  if (size === 'small') return fontDisplay('normal', theme.fontSize.xs, 18);
  return fontDisplay('normal', theme.fontSize.base, 24);
};

export default makeUiStyles<Theme, LinkStyleProps, 'root' | 'linkText' | 'icon' | 'documentIcon'>((theme: Theme) => {
  const textColor = theme.color.greys[80];
  const overlayColor = theme.color.greys[80];
  const underlineColor = theme.color.violets[100];
  const visitedColor = theme.color.raspberries[100];
  const hoverOpacity = 0.08;
  const pressedOpacity = 0.16;
  const hoverTextColor = blendHexWithOverlay(textColor, overlayColor, hoverOpacity);
  const pressedTextColor = blendHexWithOverlay(textColor, overlayColor, pressedOpacity);
  const hoverUnderlineColor = blendHexWithOverlay(underlineColor, overlayColor, hoverOpacity);
  const pressedUnderlineColor = blendHexWithOverlay(underlineColor, overlayColor, pressedOpacity);
  const visitedHoverColor = blendHexWithOverlay(visitedColor, overlayColor, hoverOpacity);
  const visitedPressedColor = blendHexWithOverlay(visitedColor, overlayColor, pressedOpacity);

  return createStyles({
    root: {
      position: 'relative',
      font: ({ size }: LinkStyleProps) => getFontSize(size, theme),
      color: textColor,
      backgroundColor: 'transparent',
      margin: 0,
      padding: 0,
      cursor: ({ disabled }: LinkStyleProps) => disabled ? 'default' : 'pointer',
      display: (props: LinkStyleProps) => hasAnyIcon(props) ? 'inline-flex' : 'inline',
      gap: (props: LinkStyleProps) => hasAnyIcon(props) ? 4 : undefined,
      textDecorationLine: (props: LinkStyleProps) =>
        hasAnyIcon(props) ? 'none' : (props.variant === 'underline' ? 'underline' : 'none'),
      textDecorationColor: underlineColor,
      textDecorationStyle: 'solid',
      textDecorationSkipInk: 'none',
      border: 'none',

      '&:visited': {
        color: visitedColor,
        textDecorationColor: visitedColor,
        '& $linkText': {
          textDecorationColor: visitedColor,
        },
        '& $icon': {
          color: visitedColor,
        },
        '& $documentIcon path': {
          fill: visitedColor,
        },
      },

      '&:hover': {
        color: hoverTextColor,
        textDecorationColor: hoverUnderlineColor,
        textDecorationLine: (props: LinkStyleProps) => hasAnyIcon(props) ? 'none' : 'underline',
        backgroundColor: hexToRgbA(overlayColor, hoverOpacity),
        '& $linkText': {
          textDecorationLine: 'underline',
          textDecorationColor: hoverUnderlineColor,
        },
      },

      '&:active, &:hover:active': {
        color: pressedTextColor,
        textDecorationColor: pressedUnderlineColor,
        backgroundColor: hexToRgbA(overlayColor, pressedOpacity),
        '& $linkText': {
          textDecorationColor: pressedUnderlineColor,
        },
      },

      '&:visited:hover': {
        color: visitedHoverColor,
        textDecorationColor: visitedHoverColor,
        '& $linkText': {
          textDecorationColor: visitedHoverColor,
        },
        '& $icon': {
          color: visitedHoverColor,
        },
        '& $documentIcon path': {
          fill: visitedHoverColor,
        },
      },

      '&:visited:active, &:visited:hover:active': {
        color: visitedPressedColor,
        textDecorationColor: visitedPressedColor,
        '& $linkText': {
          textDecorationColor: visitedPressedColor,
        },
        '& $icon': {
          color: visitedPressedColor,
        },
        '& $documentIcon path': {
          fill: visitedPressedColor,
        },
      },

      '&:focus': {
        outline: 'none',
      },

      '&:focus-visible': {
        outline: `2px solid ${textColor}`,
        outlineOffset: 2,
        borderRadius: 4,
      },

      '&[aria-disabled="true"]': {
        pointerEvents: 'none',
        opacity: 0.4,
      },
    },
    linkText: {
      textDecorationLine: ({ variant }: LinkStyleProps) => variant === 'underline' ? 'underline' : 'none',
      textDecorationColor: underlineColor,
      textDecorationStyle: 'solid',
      textDecorationSkipInk: 'none',
    },
    icon: {
      position: 'relative',
      top: -1,
      display: 'inline-flex',
      width: 16,
      height: 16,
      verticalAlign: 'middle',
      marginLeft: 4,
      color: 'inherit',
      '& path': {
        fill: 'currentColor',
      },
    },
    documentIcon: {
      width: 16,
      height: 16,
      flexShrink: 0,
      alignSelf: 'flex-start',
      marginTop: 4,
    },
  });
}, { name: 'Link', index: StylesIndexInjections.UI_ATOMS });
